import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CameraCapture } from '../components/CameraCapture';
import { LoadingScan } from '../components/LoadingScan';
import { ScanError } from '../components/ScanError';
import { parseMenu, saveMenu, ApiError } from '../lib/api';
import { ErrorCodes } from '../../shared/api';
import { extraCompressForUpload } from '../lib/compress';
import { useSettings } from '../lib/store';
import { saveMenuLocal } from '../lib/persist';

interface FailureState {
  code: string | null;
  message: string;
  files: File[];
}

export default function ScanScreen() {
  const navigate = useNavigate();
  const targetLanguage = useSettings((s) => s.targetLanguage);

  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [failure, setFailure] = useState<FailureState | null>(null);

  const previews = useMemo(() => pendingFiles?.map((f) => URL.createObjectURL(f)) ?? [], [pendingFiles]);
  useEffect(() => {
    return () => previews.forEach((p) => URL.revokeObjectURL(p));
  }, [previews]);

  const failurePreviews = useMemo(
    () => failure?.files.map((f) => URL.createObjectURL(f)) ?? [],
    [failure],
  );
  useEffect(() => {
    return () => failurePreviews.forEach((p) => URL.revokeObjectURL(p));
  }, [failurePreviews]);

  const onSubmit = async (files: File[]) => {
    setSubmitting(true);
    setPendingFiles(files);
    setFailure(null);

    // Pre-flight: scanning needs the network. Surface OFFLINE before we burn
    // the user's photo on a doomed POST. Mid-scan loss of connectivity is
    // caught in the catch below.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setFailure({ code: ErrorCodes.OFFLINE, message: 'Offline.', files });
      setSubmitting(false);
      setPendingFiles(null);
      return;
    }

    try {
      let result;
      try {
        result = await parseMenu({ files, targetLanguage });
      } catch (err) {
        // If the server says the image was too large despite client compression,
        // try one more time with aggressive compression before surfacing the
        // error to the user.
        if (err instanceof ApiError && err.code === ErrorCodes.IMAGE_TOO_LARGE) {
          const smaller = await Promise.all(files.map((f) => extraCompressForUpload(f)));
          result = await parseMenu({ files: smaller, targetLanguage });
        } else {
          throw err;
        }
      }
      await saveMenuLocal(result.menuId, result.menu, result.modelUsed);
      // Auto-pin to the user's saved menus when signed in. 401 for anon is fine;
      // their local IDB cache already covers the "I just scanned this" case.
      void saveMenu(result.menuId).catch(() => {});
      navigate(`/menu/${result.menuId}`, { replace: true });
    } catch (err) {
      // If we lost connectivity during the scan, surface OFFLINE rather than
      // the generic "Something went wrong" so the user gets actionable copy.
      const wentOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const code = wentOffline
        ? ErrorCodes.OFFLINE
        : err instanceof ApiError
          ? err.code
          : null;
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
      setFailure({ code, message, files });
      setSubmitting(false);
      setPendingFiles(null);
    }
  };

  if (submitting && pendingFiles) {
    return <LoadingScan previews={previews} />;
  }

  if (failure) {
    return (
      <ScanError
        code={failure.code}
        message={failure.message}
        thumbnails={failurePreviews}
        onRetake={() => setFailure(null)}
      />
    );
  }

  return (
    <CameraCapture
      onSubmit={onSubmit}
      onCancel={() => navigate('/')}
      busy={submitting}
      errorMessage={null}
    />
  );
}
